import * as React from 'react';

const RELOAD_AT_KEY = 'lazyWithRetry:reloadedAt';
// Wide enough that a chunk still missing after a reload surfaces as an error instead of looping.
const RELOAD_GUARD_MS = 60_000;

interface IRetryOptions {
  retries?: number;
  delayMs?: number;
}

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && (error.name === 'ChunkLoadError' || /loading (css )?chunk \S+ failed/i.test(error.message));

const reloadedRecently = (): boolean => {
  const at = Number(sessionStorage.getItem(RELOAD_AT_KEY));
  return !!at && Date.now() - at < RELOAD_GUARD_MS;
};

// A failed chunk request (network blip) is worth retrying. A chunk that no longer exists (tab opened
// before a deploy; the server answers with index.html) only comes back after a reload.
export const importWithRetry = async <T>(
  factory: () => Promise<T>,
  {retries = 2, delayMs = 500}: IRetryOptions = {},
): Promise<T> => {
  try {
    return await factory();
  } catch (error) {
    if (retries > 0) {
      await wait(delayMs);
      return importWithRetry(factory, {retries: retries - 1, delayMs: delayMs * 2});
    }

    if (isChunkLoadError(error) && !reloadedRecently()) {
      sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
      window.location.reload();
      // Never settles: the page is going away, and rejecting would flash the error fallback.
      return new Promise<T>(() => undefined);
    }

    throw error;
  }
};

export const lazyWithRetry = <T extends React.ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  factory: () => Promise<{default: T}>,
  options?: IRetryOptions,
): React.LazyExoticComponent<T> => React.lazy(() => importWithRetry(factory, options));
